/**
 * Payment: what you can pay with, what the gateway can come back with, and how
 * long a booking may sit unpaid before it lapses.
 *
 * Static seed data standing in for an API, like the rest of `src/data`.
 *
 * ---------------------------------------------------------------------------
 * NOTHING IS CHARGED
 * ---------------------------------------------------------------------------
 * There is no `react-native-razorpay` in `package.json` and no order endpoint
 * behind it. Every instrument below is made up, no card is stored anywhere,
 * and the "gateway" is a component in `src/components/payment` that resolves a
 * timer. What IS real is the shape of the flow - order id, window, verify,
 * refund-after-expiry - so that swapping the stand-in for the SDK is a change
 * to one component rather than to the whole screen.
 *
 * When the SDK does land, the only things here that survive unchanged are the
 * copy, the failure codes and the window. The instruments come from the
 * gateway's own sheet.
 */

import { IconName } from '../components/ui/Icon';
import { paymentMethods as savedInstruments } from './account';
import { BookingMode } from './booking';
import { CategoryKey } from './services';

/**
 * Whole rupees, grouped - `Intl` is not reliably present on Android JSC.
 *
 * The same one-liner as the cart's. Exported here because six payment files
 * need it and a seventh copy is how two screens end up disagreeing about what
 * a total looks like.
 */
export const rupees = (value: number) =>
  '₹' + String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// ---------------------------------------------------------------- the gateway

/*
 * Whether the customer can pay in the app is NOT decided here.
 *
 * It depends on credentials this app cannot see - the backend's `RAZORPAY_*`
 * environment - so the server is asked instead, at launch, and the answer is
 * `onlinePaymentsEnabled()` in `services/config.ts`. A constant here could
 * disagree with the server, and the expensive direction of that disagreement
 * is an app offering to charge for a booking the API will refuse with a 501.
 *
 * Everything below - the method list, the saved cards, the gateway sheet, the
 * verify steps, the expiry and refund states - is what the customer walks
 * through once that answer is true.
 */

/** Whose sheet the customer is about to see. Named in the copy, so it is here. */
export const GATEWAY = 'Razorpay';

/** The reassurance line under every method list. */
export const SECURE_NOTE = '100% Secure Payments';

/** The placeholder in the VPA field - a shape, not an address that exists. */
export const VPA_HINT = 'name@okhdfcbank';

/**
 * The handle already on the account, prefilled at the gateway.
 *
 * Read from the account's own list rather than typed again here. Account →
 * Payment Methods and checkout are two views of one set of instruments, and
 * the fastest way to look broken is to offer a card at checkout that the
 * account page has never heard of.
 */
export const savedVpa: string | undefined = savedInstruments.find(
  instrument => instrument.kind === 'upi',
)?.vpa;

// ---------------------------------------------------------------- instruments

export type PaymentMethodKey = 'upi' | 'new-card' | 'saved-card';

export type PaymentMethod = {
  key: PaymentMethodKey;
  label: string;
  blurb: string;
  icon: IconName;
  /**
   * Sits above the others under a "Recommended" eyebrow, and is what the list
   * opens on.
   *
   * Exactly one method carries this. Two would make the eyebrow a lie about
   * which one to pick, which is the only job it has.
   */
  recommended?: boolean;
};

export const paymentMethods: PaymentMethod[] = [
  {
    key: 'upi',
    label: 'UPI',
    blurb: 'Pay using any UPI app',
    icon: 'qr',
    recommended: true,
  },
  {
    key: 'new-card',
    label: 'Pay with New Card',
    blurb: 'Visa, Mastercard, RuPay, Amex',
    icon: 'creditCard',
  },
  {
    key: 'saved-card',
    label: 'Saved Cards',
    blurb: 'Use your saved card',
    icon: 'creditCard',
  },
  /*
   * There is no "Wallets" row any more.
   *
   * It offered to settle the bill from a balance that did not exist: the
   * figure came from a hardcoded instrument in `data/account.ts`, nothing was
   * ever debited, and `POST /bookings` has no wallet payment mode to send -
   * `PaymentMode` is `'cash' | 'online'` and always has been. So the row could
   * be selected, showed a balance, disabled itself when that balance was
   * "short", and then booked as cash regardless.
   *
   * Homingo Coins - the real balance, on the real ledger - are applied in the
   * CART instead, against the bill rather than as an instrument, because that
   * is what they are: a credit that reduces what is owed, not a way of paying
   * it. See `useRedemption` and the coin row in `CartScreen`.
   */
];

/** What the picker opens on. Derived, so it cannot drift from the flag above. */
export const DEFAULT_METHOD: PaymentMethodKey =
  paymentMethods.find(method => method.recommended)?.key ?? 'upi';

export const methodFor = (key: PaymentMethodKey): PaymentMethod =>
  paymentMethods.find(method => method.key === key) ?? paymentMethods[0];

export type UpiApp = {
  key: string;
  name: string;
  /** Initials for the lettermark. See `BrandMark` for why not a logo. */
  mark: string;
};

export const upiApps: UpiApp[] = [
  { key: 'gpay', name: 'G Pay', mark: 'GP' },
  { key: 'phonepe', name: 'PhonePe', mark: 'Pe' },
  { key: 'paytm', name: 'Paytm', mark: 'Pt' },
  { key: 'bhim', name: 'BHIM', mark: 'B' },
];

export type CardNetwork = 'Visa' | 'Mastercard' | 'RuPay';

export type SavedCard = {
  id: string;
  network: CardNetwork;
  /** Last four only. The rest is never in the app, and must never be. */
  last4: string;
  /** MM/YY, as printed. */
  expires: string;
  isDefault?: boolean;
};

/**
 * The account's own cards, in the shape checkout needs.
 *
 * Adapted rather than re-listed. `src/data/account.ts` is the one place a card
 * is described, so a card added there shows up at checkout without anyone
 * remembering to add it twice.
 */
export const savedCards: SavedCard[] = savedInstruments
  .filter(instrument => instrument.kind === 'card')
  .map(instrument => ({
    id: instrument.id,
    network: instrument.label as CardNetwork,
    last4: instrument.last4 ?? '',
    expires: instrument.expires ?? '',
    isDefault: instrument.isDefault,
  }));

/**
 * Which card the list opens on, or null when there are none.
 *
 * Falling back to the first is what stops the saved-cards page opening with
 * nothing selected and a button that appears to do nothing.
 *
 * NULLABLE, and typed that way deliberately. `savedCards` is derived from
 * `data/account.ts`, which is now empty - there is no vault to fill it from -
 * so `savedCards[0]` is `undefined` at runtime. This used to claim to return
 * a `SavedCard` regardless, which turned an empty list into a crash at
 * `card.network` rather than an empty state.
 */
export const defaultCard = (): SavedCard | null =>
  savedCards.find(card => card.isDefault) ?? savedCards[0] ?? null;

/*
 * `homingoWallet` and `walletApplied` used to live here, and both are gone.
 *
 * The balance was read out of a hardcoded instrument in `data/account.ts` and
 * subtracted from the cart total locally. Nothing was debited anywhere: the
 * same balance came back on the very next booking, and - the reason this was
 * found - a cancellation had nothing to return, because nothing had ever been
 * spent. The arithmetic was right and the number it worked on was invented.
 *
 * Homingo Coins replace both. They are a real ledger the server owns, read
 * through `services/wallet.ts`, quoted per-basket by `useRedemption`, sent to
 * `POST /bookings` as `coinsToRedeem`, and returned by the server's own cancel
 * handler. See the coin row in `CartScreen`.
 */

/** How an instrument reads back on a receipt - "UPI • name@okhdfcbank". */
export const describeInstrument = (
  method: PaymentMethodKey,
  detail?: string,
): string => {
  const label = method === 'upi' ? 'UPI' : methodFor(method).label;
  return detail ? `${label} • ${detail}` : label;
};

// ------------------------------------------------------------------ outcomes

export type FailureCode = 'UPI_TIMEOUT' | 'BANK_DECLINED' | 'CARD_DECLINED';

export type Failure = {
  code: FailureCode;
  /** What went wrong, in the customer's words. */
  title: string;
  /** What to do about it. Every one of these must name a next step. */
  detail: string;
};

export const failures: Record<FailureCode, Failure> = {
  UPI_TIMEOUT: {
    code: 'UPI_TIMEOUT',
    title: 'UPI transaction failed',
    detail: 'Please try again with a different payment method.',
  },
  BANK_DECLINED: {
    code: 'BANK_DECLINED',
    title: 'Bank declined the transaction',
    detail: 'Please ensure sufficient balance or try another UPI ID.',
  },
  CARD_DECLINED: {
    code: 'CARD_DECLINED',
    title: 'Card declined by issuer',
    detail: 'Please try another card or pay using UPI.',
  },
};

/**
 * Which failure a method produces.
 *
 * A real gateway hands back its own code; this maps the instrument to the
 * failure a customer would plausibly see from it, so the reason card never
 * says "UPI timeout" about a card payment.
 */
export const failureFor = (method: PaymentMethodKey): Failure =>
  /* No `wallet` branch: coins are not a payment INSTRUMENT any more - they are
     a discount the server applies to the order before a gateway sees it, so
     there is no such thing as a wallet payment failing. See the note where
     `homingoWallet` used to be. */
  method === 'upi' ? failures.UPI_TIMEOUT : failures.CARD_DECLINED;

/**
 * The server-side check between the gateway saying yes and the app agreeing.
 *
 * A gateway callback is a message from the customer's own device, so it is not
 * proof of anything until the signature has been checked against the order.
 * The steps are shown rather than hidden behind a spinner because the wait is
 * the security feature, and a spinner asks the customer to trust it blindly.
 */
export const verificationSteps: string[] = [
  'Verifying payment status',
  'Validating secure signature',
  'Confirming with bank',
];

/** How long each verification step is held before the next ticks over. */
export const VERIFY_STEP_MS = 900;

// -------------------------------------------------------------- the window

/**
 * How long a booking may sit unpaid before it lapses, in seconds.
 *
 * The slot is held while the customer pays and released when this runs out -
 * otherwise an abandoned checkout keeps a professional's morning blocked. The
 * countdown is shown from the summary onward so the deadline is never a
 * surprise.
 */
export const PAYMENT_WINDOW_SECONDS = 15 * 60;

/** Below this the countdown turns to the danger ink. */
export const WINDOW_WARNING_SECONDS = 2 * 60;

/** `m:ss`, the shape a countdown is read in. */
export const countdown = (seconds: number): string => {
  const safe = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

// ------------------------------------------------------------ ids and stamps

/**
 * The reference the customer quotes, in the same shape as the seeded bookings.
 *
 * Time-based rather than random so two bookings made in one session cannot
 * collide, and so support can read an order back to roughly when it was made.
 * A real one comes from the server, which is the only thing that can promise
 * it is unique.
 */
export const newReference = (): string => `#HOM${String(Date.now()).slice(-5)}`;

const bare = (reference: string) => reference.replace(/^#/, '');

/** Razorpay's own id shape - `order_` prefixed, and what support asks for. */
export const orderIdFor = (reference: string): string => `order_${bare(reference)}`;

export const refundIdFor = (reference: string): string => `rfnd_${bare(reference)}`;

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "12 Aug 2026, 10:02 AM" - hand-rolled, `Intl` being unreliable on JSC. */
export const stamp = (at: Date): string => {
  const hours = at.getHours();
  const suffix = hours < 12 ? 'AM' : 'PM';
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  const mins = String(at.getMinutes()).padStart(2, '0');

  return `${at.getDate()} ${MONTHS[at.getMonth()]} ${at.getFullYear()}, ${twelve}:${mins} ${suffix}`;
};

// -------------------------------------------------------------- what is owed

export type PriceLine = {
  label: string;
  amount: number;
  /** Rendered as "−₹x": a coupon, a credit, anything that comes off. */
  credit?: boolean;
};

/**
 * A server decimal string, rounded to the whole rupee this app displays.
 *
 * The one arithmetic this file does on a checkout figure: `Number()` to parse
 * it and `Math.round()` because the app never shows paise. Nothing here adds,
 * subtracts or otherwise recombines a value the server sent - it turns ONE
 * string into the ONE number `rupees()` already knows how to format.
 */
export const wholeRupees = (amount: string): number => Math.round(Number(amount));

/**
 * `CheckoutQuoteDto.lines`, read into the shape this app already renders.
 *
 * ---------------------------------------------------------------------------
 * A SIGN READ, NOT A SUM
 * ---------------------------------------------------------------------------
 * The quote's own `lines` are already the finished, itemized bill - Items,
 * Fee, Subscription, Coupon, Coins, Tip, each one aggregated across the whole
 * basket, in the order to show them. This does not add anything to anything:
 * it reads the SIGN off each decimal string (negative is a discount) and
 * drops it, because `PriceLine.credit` is a boolean and `rupees()` takes an
 * unsigned amount. The list itself, and its total, are the server's.
 */
export const linesFromQuote = (
  lines: { label: string; amount: string }[],
): PriceLine[] =>
  lines.map(line => {
    const signed = Number(line.amount);
    return {
      label: line.label,
      amount: Math.round(Math.abs(signed)),
      credit: signed < 0,
    };
  });

/**
 * Everything the payment screens need about the booking being paid for.
 *
 * Built by the cart and passed down whole, rather than recomputed here. The
 * cart is where the coupon, the tip and the fee live, and a second place that
 * derives a total is a second place that can disagree with the button the
 * customer already read.
 */
/**
 * A repeating arrangement, in the terms the server accepts.
 *
 * ---------------------------------------------------------------------------
 * A BAND IS NOT A TIME
 * ---------------------------------------------------------------------------
 * The cart asks for a band - "Afternoon (12 PM – 5 PM)" - and the plan
 * endpoint wants `HH:mm`. The band's OPENING hour is what gets sent, because
 * it is the only reading that cannot promise the customer something earlier
 * than they chose. Anything inside the band would be arbitrary; anything after
 * it starts the visit outside the window they picked.
 */
export type OrderRecurrence = {
  frequency: 'weekly' | 'biweekly' | 'monthly';
  /** Sunday = 0, matching the server. */
  daysOfWeek: number[];
  /** `HH:mm`, the opening hour of the chosen band. */
  timeOfDay: string;
  /** `YYYY-MM-DD` - the first visit. */
  startDate: string;
};

export type PaymentOrder = {
  /** "#HOM12846" - what the customer quotes. */
  reference: string;
  /** "order_HOM12846" - what the gateway and support quote. */
  orderId: string;
  /** The leading service, for the heading. */
  title: string;
  /**
   * The trade of the leading service.
   *
   * Carried so the booking this becomes has a category without payment having
   * to go back to the catalogue to look one up. A basket can span trades; this
   * is the one the booking files under, the same way the title is the one
   * service it is named after.
   */
  category: CategoryKey;
  /** Every service in the basket, for the collapsible list. */
  services: string[];
  /**
   * What actually gets booked - one entry per unit, so a quantity of 2
   * appears twice. `services` above is copy for the collapsible list and
   * books nothing.
   *
   * ---------------------------------------------------------------------
   * WHY THIS IS NOT A LIST OF IDS ANY MORE
   * ---------------------------------------------------------------------
   * It was `serviceIds: string[]` until services could be sold by the
   * hour. The hours are per unit and have to travel with the id they belong
   * to: a parallel array would be correct exactly as long as nobody reordered
   * or filtered either half, and the failure mode is a booking made for the
   * wrong length with nothing anywhere reading as wrong.
   */
  items: { serviceId: string; hours?: number }[];
  /** "Cleaning, Plumbing" - the trades involved. */
  trades: string;
  /** When it happens, already formatted. */
  when: string;
  /**
   * Instant, a slot, or a repeating schedule - chosen in the cart's
   * "Booking Type" step. Carried through so the booking this becomes can be
   * filed under the right status: a recurring one lives apart from a live
   * one, on `bookings.ts`'s own status axis.
   */
  mode: BookingMode;
  /**
   * The repeat pattern - "Every Thu, Afternoon (12 PM – 5 PM)" - set only
   * when `mode` is `'recurring'`. `when` is the next visit; this is how often
   * the ones after it come, and a recurring booking needs both.
   */
  cadence?: string;
  /**
   * The same pattern, structured, for the server.
   *
   * `cadence` above is a sentence and cannot be sent - the plan endpoint wants
   * a frequency, weekdays and a time. Carried alongside rather than parsed back
   * out of the sentence, which would make the copy load-bearing.
   */
  recurrence?: OrderRecurrence;
  /**
   * ISO instant the visit should start - set when `mode` is `'schedule'` or
   * `'recurring'`, omitted for `'instant'`. What `createBooking` sends as
   * `slotStartAt`.
   */
  slotStartAt?: string;
  /** "2–3 hrs (Estimated)". */
  duration: string;
  address: string;
  /**
   * The breakdown, in the order the cart showed it.
   *
   * Read off `CheckoutQuoteDto.lines` (or `CheckoutDto.lines` once placed) via
   * `linesFromQuote` - not computed here. The cart and the payment screen must
   * show the same figures for the same basket, and the only way to guarantee
   * that is for both to read the one bill the server priced.
   */
  lines: PriceLine[];
  /** `totalAmount` from the quote/place response, parsed with `wholeRupees` -
      never a sum this app performed on the lines above. */
  total: number;
  /**
   * The coupon code the cart had applied, if any - typed, or picked from the
   * offers list. Carried through to `POST checkout` so the order is placed
   * against the same code the quote priced; the server re-validates it rather
   * than trusting that the quote's answer still holds.
   */
  code?: string;
  /** The tip, as the decimal string `POST checkout/quote` and `POST checkout`
      both take - e.g. `"50.00"`. Absent when no tip was chosen. */
  tipAmount?: string;
  /**
   * Homingo Coins the cart applied, as a COUNT - see `createBooking`.
   *
   * Carried on the order rather than re-quoted at payment for the same reason
   * `serviceIds` is: this is the snapshot taken when "Proceed to Pay" was
   * tapped - specifically `CheckoutQuoteDto.coinsRedeemed` off the last quote
   * the customer actually saw - and re-asking at the moment of booking could
   * return a different allowance than the one the customer agreed to and was
   * charged against. Absent when no coins were applied.
   */
  coinsToRedeem?: number;
  /**
   * The customer ticked the booking terms in the cart.
   *
   * Carried on the order rather than re-asked at payment: the box is in the
   * cart, and the screen that charges them is not where consent is taken.
   * The server stamps the time - a phone's clock proves nothing.
   */
  acceptedTerms?: boolean;
};

/** Turns whole minutes into the band a visit is quoted in. */
export const durationBand = (minutes: number): string => {
  const hours = minutes / 60;
  const low = Math.max(1, Math.floor(hours));
  const high = Math.max(low + 1, Math.ceil(hours));
  return `${low}–${high} hrs (Estimated)`;
};
